import { faFileSlash, faFileText } from '@fortawesome/pro-solid-svg-icons'
import { type ReactElement, useMemo } from 'react'
import { useApplicant } from '~/api/queries/applicant'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { useApplicantAttachmentContent } from '~/domains/applicants/queries/applicantAttachments'
import { AttachmentsViewer } from '~/domains/attachments/components/AttachmentsViewer'
import type { AttachmentType } from '~/domains/attachments/types'
import { useApplication } from '../queries/useApplication'

export const ApplicationAttachments = ({
  applicationId
}: {
  applicationId: number
}): ReactElement => {
  const { data: application, isLoading } = useApplication({ id: applicationId })

  const { data: applicant, isLoading: isApplicantLoading } = useApplicant({
    id:
      application?.attachments.length === 0
        ? application.applicant?.id
        : undefined
  })
  const applicantResumeId = applicant?.resume?.id
  const { data: applicantResumeContent, isLoading: isApplicantResumeLoading } =
    useApplicantAttachmentContent({
      attachmentId: applicantResumeId ?? 0,
      enabled: !!applicantResumeId
    })

  const combinedAttachments = useMemo((): AttachmentType[] => {
    if (!application) return []

    const attachments: AttachmentType[] = application.attachments.map(
      (attachment) => ({
        id: attachment.id,
        name: attachment.object?.filename ?? '',
        icon: 'attachment',
        content: attachment.object?.base64Content,
        url: attachment.object?.url,
        mimeType: attachment.object?.mime,
        type: 'attachment'
      })
    )

    if (attachments.length === 0 && applicantResumeContent) {
      attachments.push({
        id: applicantResumeContent.id,
        name: applicantResumeContent.object.filename ?? '',
        content: applicantResumeContent.object.base64Content,
        url: applicantResumeContent.object.url,
        mimeType: applicantResumeContent.object.mime,
        type: 'attachment'
      })
    }

    // Add email body as HTML attachment if it exists
    if (application.jobmail.body && application.jobmail.body.trim() !== '') {
      attachments.push({
        id: -1, // Virtual attachment with negative ID
        name: `Email - ${application.jobmail.subject || ''}`,
        type: 'attachment',
        content: application.jobmail.body,
        formatInfo: {
          format: 'html',
          supportsZoom: false,
          icon: faFileText,
          extension: 'html'
        }
      })
    }

    return attachments
  }, [application, applicantResumeContent])

  if (
    isLoading ||
    !application ||
    isApplicantLoading ||
    isApplicantResumeLoading
  ) {
    return <Shimmer height="100%" />
  }

  if (combinedAttachments.length === 0) {
    return (
      <NoResult icon={faFileSlash} message="Aucun fichier joint à afficher" />
    )
  }

  return (
    <AttachmentsViewer
      attachments={combinedAttachments}
      isLoading={isLoading}
      defaultAttachmentId={combinedAttachments[0]?.id}
    />
  )
}
